import { Badge } from '@aitoken/ui/components/badge';
import { Button } from '@aitoken/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aitoken/ui/components/dropdown-menu';
import { Kbd, KbdGroup } from '@aitoken/ui/components/kbd';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@aitoken/ui/components/sidebar';
import { LogoMark } from '@aitoken/ui/components/site';
import { Tooltip, TooltipContent, TooltipTrigger } from '@aitoken/ui/components/tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Bell, ChevronDown, LogOut, Search, ShieldCheck } from 'lucide-react';
import { lazy, Suspense, useEffect, useState, type CSSProperties } from 'react';
import { api, setCsrfToken } from '@/lib/api';
import { envLabel, hasPermission, useMe } from '@/lib/auth';
import { alertsQuery } from '@/lib/queries';
import { ROLE_LABEL } from '@/lib/labels';
import { NAV, navVisible, type NavPath } from '@/nav';
import { ThemeMenu } from './theme-menu';

const CommandSearch = lazy(() => import('./command-search').then((m) => ({ default: m.CommandSearch })));

function readSidebarCookie(): boolean {
  const match = document.cookie.match(/(?:^|; )sidebar_state=(true|false)/);
  return match ? match[1] === 'true' : true;
}

function isActive(pathname: string, to: NavPath) {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function useOpenAlertCount(enabled: boolean) {
  return useQuery({
    ...alertsQuery('open'),
    enabled,
    refetchInterval: 60_000,
    meta: { silent: true },
    select: (res) => res.data.length,
  });
}

function AppSidebar({ openAlerts }: { openAlerts: number | undefined }) {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-3 pt-3 pb-1">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              size="lg"
              tooltip="aitoken.tools 运营后台"
              className="h-12 gap-2.5 rounded-xl px-1.5 hover:bg-transparent active:bg-transparent"
            >
              <Link to="/">
                {/* 菜单按钮会把直接子级 svg 压成 16px：标志要保持 32px；收起时文字整块隐藏，避免被截出碎片 */}
                <LogoMark className="size-8!" />
                <span className="flex min-w-0 flex-col gap-1 leading-none group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-display text-[18px] font-bold tracking-[-0.03em] text-foreground">
                    aitoken<span className="text-muted-foreground">.tools</span>
                  </span>
                  <span className="truncate text-xs text-muted-foreground">运营后台</span>
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {NAV.map((group) => {
          const items = group.items.filter((item) => navVisible(me, item));
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={group.label} className="px-3 pt-4 pb-0">
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild isActive={isActive(pathname, item.to)} tooltip={item.label}>
                        <Link to={item.to}>
                          <item.icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                      {item.to === '/alerts' && openAlerts ? (
                        <SidebarMenuBadge className="tabular text-danger">{openAlerts}</SidebarMenuBadge>
                      ) : null}
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

/**
 * 站点标识（docs/07 §1“站点永远可见”）：顶栏左侧的站点印记，站点色圆点 + 站名 + 环境，常驻吸顶顶栏。
 * 按用户要求不再有页面顶部的站点色条。
 */
function SiteSeal({ site, environment }: { site: 'cn' | 'global'; environment: string }) {
  const cn_ = site === 'cn';
  return (
    <span
      className="admin-surface inline-flex h-8 shrink-0 items-center gap-2 rounded-full bg-card px-3 text-sm font-semibold whitespace-nowrap"
      aria-label={`当前站点：${cn_ ? '国内站' : '海外站'}，${environment}环境`}
    >
      <span
        className="size-2 rounded-full bg-site-live"
        aria-hidden
      />
      <span>{cn_ ? '国内站' : '海外站'}</span>
      <span className="hidden font-normal text-muted-foreground sm:inline">{environment}</span>
    </span>
  );
}

function AdminMenu() {
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/admin/auth/logout'),
    meta: { silent: true },
    onSettled: () => {
      setCsrfToken(null);
      queryClient.clear();
      void navigate({ to: '/login', search: {} });
    },
  });
  const name = me.email.split('@')[0] ?? me.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-2 pr-2.5 pl-1" aria-label={`当前管理员 ${me.email}`}>
          <span
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-raised font-display text-[13px] font-semibold"
            aria-hidden
          >
            {name.slice(0, 1).toUpperCase()}
          </span>
          <span className="hidden max-w-40 truncate md:inline">{name}</span>
          <span className="hidden text-muted-foreground md:inline">{ROLE_LABEL[me.role]}</span>
          <ChevronDown className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate">{me.email}</span>
          <span className="text-xs font-normal text-muted-foreground">
            {ROLE_LABEL[me.role]}，{me.permissions.length} 项权限
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {hasPermission(me, 'audit.read') ? (
            <DropdownMenuItem asChild>
              <Link to="/audit">
                <ShieldCheck />
                审计日志
              </Link>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => logout.mutate()} disabled={logout.isPending}>
            <LogOut />
            退出登录
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TopBar({
  openAlerts,
  canSeeAlerts,
  onSearch,
}: {
  openAlerts: number | undefined;
  canSeeAlerts: boolean;
  onSearch: () => void;
}) {
  const me = useMe();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-scrolled={scrolled}
      className="admin-bar sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 px-3 md:gap-3 md:px-8"
    >
      <SidebarTrigger className="-ml-1 size-9 rounded-full text-muted-foreground hover:text-foreground" />
      <SiteSeal site={me.site_id} environment={envLabel(me.environment)} />

      <button
        type="button"
        onClick={onSearch}
        className="mx-auto flex h-9 w-full max-w-md min-w-0 items-center gap-2 rounded-full bg-card admin-surface px-4 text-left text-sm text-muted-foreground transition-colors hover:bg-popover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        aria-label="全局搜索"
        aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="hidden truncate sm:inline">搜索用户邮箱、任务 ID、素材 ID、线下单号</span>
        <span className="truncate sm:hidden">搜索</span>
        <KbdGroup className="ml-auto hidden sm:inline-flex">
          <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>K</Kbd>
        </KbdGroup>
      </button>

      {canSeeAlerts ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="relative text-muted-foreground hover:text-foreground"
            >
              <Link to="/alerts" search={{ status: 'open' }} aria-label={`待处理告警 ${openAlerts ?? 0} 条`}>
                <Bell />
                {openAlerts ? (
                  <Badge className="absolute top-0 right-0 h-[18px] min-w-[18px] rounded-full bg-danger px-1 text-[12px] leading-none font-semibold text-danger-foreground tabular">
                    {openAlerts > 99 ? '99+' : openAlerts}
                  </Badge>
                ) : null}
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>待处理告警</TooltipContent>
        </Tooltip>
      ) : null}
      <ThemeMenu />
      <AdminMenu />
    </header>
  );
}

export function AppShell() {
  const me = useMe();
  const canSeeAlerts = hasPermission(me, ['alert.write', 'dashboard.read']);
  const { data: openAlerts } = useOpenAlertCount(canSeeAlerts);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoaded, setSearchLoaded] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchLoaded(true);
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.title = `${me.site_id === 'cn' ? '国内站' : '海外站'} · aitoken.tools 运营后台`;
  }, [me.site_name]);

  return (
    <>
      <SidebarProvider
        defaultOpen={readSidebarCookie()}
        style={{ '--sidebar-width': '15rem' } as CSSProperties}
      >
        <AppSidebar openAlerts={openAlerts} />
        <SidebarInset className="min-w-0">
          <TopBar
            openAlerts={openAlerts}
            canSeeAlerts={canSeeAlerts}
            onSearch={() => {
              setSearchLoaded(true);
              setSearchOpen(true);
            }}
          />
          <div className="flex-1 px-4 pt-4 pb-16 md:px-8">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
      {searchLoaded ? (
        <Suspense fallback={null}>
          <CommandSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </Suspense>
      ) : null}
    </>
  );
}
